import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Certificate-of-Analysis dialog used by the product card + PDP.
 *
 * Renders a small "COA" pill button; clicking it opens a modal that
 * lazy-loads `/api/products/{id}/coa` once. If the product hasn't been COA'd
 * yet we show a graceful "coming soon" placeholder. The lazy-load keeps the
 * catalog grid render fast — no upfront fetch per card.
 */
export function CoaDialog({ product }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ coa_image: null, coa_storage_path: null, loaded: false });

  const openCoa = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
    if (data.loaded) return;
    try {
      const { data: payload } = await axios.get(`${API}/products/${product.id}/coa`);
      setData({ coa_image: payload.coa_image, coa_storage_path: payload.coa_storage_path, loaded: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('coa fetch failed:', err?.message);
      setData({ coa_image: null, coa_storage_path: null, loaded: true });
    }
  };

  const url = data.coa_storage_path ? `${API}/files/${data.coa_storage_path}` : data.coa_image;

  return (
    <>
      <button
        type="button"
        onClick={openCoa}
        className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-md bg-white/90 backdrop-blur-sm px-2 py-0.5 text-[10px] font-semibold border border-border hover:bg-white transition-colors"
        data-testid={`coa-badge-${product.id}`}
      >
        <FileText className="h-3 w-3" /> COA
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`coa-dialog-${product.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading">
              Certificate of Analysis — {product.name}
            </DialogTitle>
          </DialogHeader>
          {!data.loaded ? (
            <div className="h-48 bg-muted animate-pulse rounded" />
          ) : url ? (
            <img src={url} alt="Certificate of Analysis" className="w-full rounded-lg border" />
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-foreground mb-2">COA Coming Soon</h3>
              <p className="text-sm text-muted-foreground font-body">
                Our latest batch is currently being tested by our third-party lab, please check back later for results.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
